import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Col, Button } from "reactstrap";
import { Formik, Form } from "formik";
import Loader from "../../../components/Loader/Loader";
import * as Yup from "yup";
import FormField from "../../../components/FormField/FormField";
import { getSEO, saveSEO } from "../../../actions/seoAction";
import isEmpty from "../../../validation/is-empty";
import { notification } from "../../../utils/utility";
import Filter from "../../../components/Filter/Filter";
import Tooltip from "../../../components/Tooltip";
import useFeatureRedirect from "../../../hooks/useFeatureRedirect";
import { FEATURE_IDS } from "../../../constants/featureAccess";

const PIXEL_RULES = {
  facebook_pixel_id: {
    regex: /^\d{15,16}$/,
    message: "Facebook Pixel ID must be 15–16 digits",
  },
  twitter_pixel_id: {
    regex: /^[A-Za-z0-9]{5,64}$/,
    message: "Twitter Pixel ID format is invalid",
  },
  linkedin_pixel_id: {
    regex: /^\d{5,20}$/,
    message: "LinkedIn Pixel ID must be a numeric Partner ID",
  },
  pinterest_pixel_id: {
    regex: /^[A-Za-z0-9]{5,64}$/,
    message: "Pinterest Pixel ID format is invalid",
  },
  quora_pixel_id: {
    regex: /^[A-Za-z0-9\-_]{5,64}$/,
    message: "Quora Pixel ID format is invalid",
  },
  bing_pixel_id: {
    regex: /^\d{5,20}$/,
    message: "Bing UET Tag ID must be numeric",
  },
  google_adwords_pixel_id: {
    regex: /^(AW-)?\d{6,20}$/i,
    message: "Google Ads ID must look like AW-123456789",
  },
  google_tm_pixel_id: {
    regex: /^GTM-[A-Z0-9]{4,12}$/i,
    message: "Google Tag Manager ID must look like GTM-XXXXXXX",
  },
  google_analytics_id: {
    regex: /^(G-[A-Z0-9]{6,12}|UA-\d{4,12}-\d{1,4})$/i,
    message: "Google Analytics ID must look like G-XXXXXXXX or UA-XXXXXXX-X",
  },
};

const optionalPixel = (regex, message) =>
  Yup.string()
    .transform((value) => (value == null ? "" : String(value).trim()))
    .test("pixel-format", message, (value) => {
      if (!value) return true;
      return regex.test(value);
    });

/** Drop legacy invalid IDs so Save is not blocked by pre-validation data. */
const sanitizeLoadedPixel = (value, regex) => {
  const trimmed = value == null ? "" : String(value).trim();
  if (!trimmed) return "";
  return regex.test(trimmed) ? trimmed : "";
};

const validation = Yup.object().shape({
  title: Yup.string().trim().required("Title is required").max(255, "Title is too long"),
  description: Yup.string().trim().max(5000, "Description is too long"),
  keywords: Yup.string().trim().max(1000, "Keywords are too long"),
  facebook_pixel_id: optionalPixel(
    PIXEL_RULES.facebook_pixel_id.regex,
    PIXEL_RULES.facebook_pixel_id.message
  ),
  twitter_pixel_id: optionalPixel(
    PIXEL_RULES.twitter_pixel_id.regex,
    PIXEL_RULES.twitter_pixel_id.message
  ),
  linkedin_pixel_id: optionalPixel(
    PIXEL_RULES.linkedin_pixel_id.regex,
    PIXEL_RULES.linkedin_pixel_id.message
  ),
  pinterest_pixel_id: optionalPixel(
    PIXEL_RULES.pinterest_pixel_id.regex,
    PIXEL_RULES.pinterest_pixel_id.message
  ),
  quora_pixel_id: optionalPixel(
    PIXEL_RULES.quora_pixel_id.regex,
    PIXEL_RULES.quora_pixel_id.message
  ),
  bing_pixel_id: optionalPixel(
    PIXEL_RULES.bing_pixel_id.regex,
    PIXEL_RULES.bing_pixel_id.message
  ),
  google_adwords_pixel_id: optionalPixel(
    PIXEL_RULES.google_adwords_pixel_id.regex,
    PIXEL_RULES.google_adwords_pixel_id.message
  ),
  google_tm_pixel_id: optionalPixel(
    PIXEL_RULES.google_tm_pixel_id.regex,
    PIXEL_RULES.google_tm_pixel_id.message
  ),
  google_analytics_id: optionalPixel(
    PIXEL_RULES.google_analytics_id.regex,
    PIXEL_RULES.google_analytics_id.message
  ),
});

let initialValues = {
  title: "",
  description: "",
  keywords: "",
  facebook_pixel_id: "",
  twitter_pixel_id: "",
  linkedin_pixel_id: "",
  pinterest_pixel_id: "",
  quora_pixel_id: "",
  bing_pixel_id: "",
  google_adwords_pixel_id: "",
  google_tm_pixel_id: "",
  google_analytics_id: "",
};

const SEO = (props) => {
  const [formValues, setFormValues] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);

  useEffect(() => {
    const { getSEO } = props;
    setPageLoading(true);
    getSEO({
      data: {},
      onSuccess: function (details) {
        if (!isEmpty(details)) {
          setFormValues({
            id: details.id,
            title: details.title || "",
            description: details.description || "",
            keywords: details.keywords || "",
            facebook_pixel_id: sanitizeLoadedPixel(
              details.facebook_pixel_id,
              PIXEL_RULES.facebook_pixel_id.regex
            ),
            twitter_pixel_id: sanitizeLoadedPixel(
              details.twitter_pixel_id,
              PIXEL_RULES.twitter_pixel_id.regex
            ),
            linkedin_pixel_id: sanitizeLoadedPixel(
              details.linkedin_pixel_id,
              PIXEL_RULES.linkedin_pixel_id.regex
            ),
            pinterest_pixel_id: sanitizeLoadedPixel(
              details.pinterest_pixel_id,
              PIXEL_RULES.pinterest_pixel_id.regex
            ),
            quora_pixel_id: sanitizeLoadedPixel(
              details.quora_pixel_id,
              PIXEL_RULES.quora_pixel_id.regex
            ),
            bing_pixel_id: sanitizeLoadedPixel(
              details.bing_pixel_id,
              PIXEL_RULES.bing_pixel_id.regex
            ),
            google_adwords_pixel_id: sanitizeLoadedPixel(
              details.google_adwords_pixel_id,
              PIXEL_RULES.google_adwords_pixel_id.regex
            ),
            google_tm_pixel_id: sanitizeLoadedPixel(
              details.google_tm_pixel_id,
              PIXEL_RULES.google_tm_pixel_id.regex
            ),
            google_analytics_id: sanitizeLoadedPixel(
              details.google_analytics_id,
              PIXEL_RULES.google_analytics_id.regex
            ),
          });
        }
        setPageLoading(false);
      },
      onError: function () {
        setPageLoading(false);
      },
    });
  }, []);

  return (
    <section>
      <Loader isShowLoader={pageLoading} />
      <div>
        <div className="eventdetailsaddbox rd_noshadow">
          <Filter
            name="Marketing Tools"
            subtitle="Set how your booking pages appear in search and ads."
          />

          <div className="rd_vacationfilterpart rd_specialcenterthingim rd_specialcenterthingimte market_tools">
            <Formik
              validationSchema={validation}
              initialValues={formValues || initialValues}
              enableReinitialize
              onSubmit={(data) => {
                const { saveSEO } = props;
                setPageLoading(true);

                saveSEO({
                  data: {
                    ...data,
                  },
                  onSuccess: (response) => {
                    if (response.success) {
                      if (response.data) {
                        const { data: responseData } = response.data;
                        if (responseData && responseData.id) {
                          setFormValues((prev) => ({
                            ...(prev || data),
                            id: responseData.id,
                          }));
                        }
                      }
                      notification.success(response.message);
                    }
                    setPageLoading(false);
                  },
                  onError: (error) => {
                    const message =
                      typeof error === "string"
                        ? error
                        : "An error occured during save, Please try later";
                    notification.error(message);
                    setPageLoading(false);
                  },
                });
              }}
            >
              {(formProps) => {
                const { values, errors, touched, handleChange } = formProps;

                return (
                  <>
                    <Form style={{ width: "100%" }}>
                      <div className="rd_vacationfilterpart rd_specialcenterthingim">
                        <div className="rd_seotoppart1contflitem1">
                          <h3 className="page_title">
                            Search Engines Optimization (SEO):
                          </h3>
                          <div className="rd_seoinfopa">
                            <label className="form-label">
                              Title * <Tooltip code="P22F1" />{" "}
                            </label>
                            <FormField
                              type="text"
                              name="title"
                              placeholder="Title"
                              fieldClasses="rd_adddayofinput"
                              value={values.title}
                              errors={errors}
                              touched={touched}
                              onChange={({ target }) => {
                                const { value } = target;
                                handleChange({
                                  target: { name: "title", value },
                                });
                              }}
                            />

                            <label className="form-label">
                              Description <Tooltip code="P22F2" />
                            </label>
                            <FormField
                              component="textarea"
                              name="description"
                              placeholder="Description"
                              fieldClasses="rd_adddayofinput mb-3"
                              value={values.description}
                              errors={errors}
                              touched={touched}
                              onChange={({ target }) => {
                                const { value } = target;
                                handleChange({
                                  target: { name: "description", value },
                                });
                              }}
                            />
                            <label className="form-label">
                              Keywords <Tooltip code="P22F3" />
                            </label>

                            <FormField
                              type="text"
                              name="keywords"
                              placeholder="Keywords, Keyword 1, Keyword 2"
                              fieldClasses="rd_adddayofinput"
                              value={values.keywords}
                              errors={errors}
                              touched={touched}
                              onChange={({ target }) => {
                                const { value } = target;
                                handleChange({
                                  target: { name: "keywords", value },
                                });
                              }}
                            />
                          </div>
                        </div>

                        <div className="rd_seotoppart1contflitem2">
                          {values.title != "" ? (
                            <div>
                              <div id="searchresultsarea">
                                <div className="searchresult">
                                  <h2>{values.title}</h2>
                                  <a className="searchresulta">
                                    https://en.wikipedia.org/wiki/Lock_(computer_science)
                                  </a>
                                  <button>▼</button>
                                  <p>{values.description}</p>
                                  <p>{values.keywords}</p>
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="rd_vacationfilterpart rd_noflex">
                        <div className="rd_pixelretargetingthning">
                          <h3 className="page_title">Remarketing Pixels</h3>

                          <div className="rd_pixelinpucointret">
                            <div className="rd_flexpoarttpixelreta">
                              <div className="rd_flexpoarttpixelretaitem">
                                <p>
                                  <span className="rd_retagpixico rd_retagpixico-facebook"></span>
                                  Facebook Pixel <Tooltip code="P22F5" />
                                </p>
                              </div>
                              <div className="rd_flexpoarttpixelretaitem">
                                <FormField
                                  type="text"
                                  name="facebook_pixel_id"
                                  placeholder="Pixel Id"
                                  fieldClasses="rd_adddayofinput"
                                  value={values.facebook_pixel_id}
                                  errors={errors}
                                  touched={touched}
                                  onChange={({ target }) => {
                                    const { value } = target;
                                    handleChange({
                                      target: {
                                        name: "facebook_pixel_id",
                                        value,
                                      },
                                    });
                                  }}
                                />
                              </div>
                            </div>

                            <div className="rd_flexpoarttpixelreta">
                              <div className="rd_flexpoarttpixelretaitem">
                                <p>
                                  <span className="rd_retagpixico rd_retagpixico-twitter"></span>
                                  Twitter <Tooltip code="P22F6" />
                                </p>
                              </div>
                              <div className="rd_flexpoarttpixelretaitem">
                                <FormField
                                  type="text"
                                  name="twitter_pixel_id"
                                  placeholder="Pixel Id"
                                  fieldClasses="rd_adddayofinput"
                                  value={values.twitter_pixel_id}
                                  errors={errors}
                                  touched={touched}
                                  onChange={({ target }) => {
                                    const { value } = target;
                                    handleChange({
                                      target: {
                                        name: "twitter_pixel_id",
                                        value,
                                      },
                                    });
                                  }}
                                />
                              </div>
                            </div>

                            <div className="rd_flexpoarttpixelreta">
                              <div className="rd_flexpoarttpixelretaitem">
                                <p>
                                  <span className="rd_retagpixico rd_retagpixico-linkedin"></span>
                                  LinkedIn <Tooltip code="P22F7" />
                                </p>
                              </div>
                              <div className="rd_flexpoarttpixelretaitem">
                                <FormField
                                  type="text"
                                  name="linkedin_pixel_id"
                                  placeholder="Pixel Id"
                                  fieldClasses="rd_adddayofinput"
                                  value={values.linkedin_pixel_id}
                                  errors={errors}
                                  touched={touched}
                                  onChange={({ target }) => {
                                    const { value } = target;
                                    handleChange({
                                      target: {
                                        name: "linkedin_pixel_id",
                                        value,
                                      },
                                    });
                                  }}
                                />
                              </div>
                            </div>

                            <div className="rd_flexpoarttpixelreta">
                              <div className="rd_flexpoarttpixelretaitem">
                                <p>
                                  <span className="rd_retagpixico rd_retagpixico-pinterest"></span>
                                  Pinterest <Tooltip code="P22F8" />
                                </p>
                              </div>
                              <div className="rd_flexpoarttpixelretaitem">
                                <FormField
                                  type="text"
                                  name="pinterest_pixel_id"
                                  placeholder="Pixel Id"
                                  fieldClasses="rd_adddayofinput"
                                  value={values.pinterest_pixel_id}
                                  errors={errors}
                                  touched={touched}
                                  onChange={({ target }) => {
                                    const { value } = target;
                                    handleChange({
                                      target: {
                                        name: "pinterest_pixel_id",
                                        value,
                                      },
                                    });
                                  }}
                                />
                              </div>
                            </div>

                            <div className="rd_flexpoarttpixelreta">
                              <div className="rd_flexpoarttpixelretaitem">
                                <p>
                                  <span className="rd_retagpixico rd_retagpixico-quora"></span>
                                  Quora <Tooltip code="P22F9" />
                                </p>
                              </div>
                              <div className="rd_flexpoarttpixelretaitem">
                                <FormField
                                  type="text"
                                  name="quora_pixel_id"
                                  placeholder="Pixel Id"
                                  fieldClasses="rd_adddayofinput"
                                  value={values.quora_pixel_id}
                                  errors={errors}
                                  touched={touched}
                                  onChange={({ target }) => {
                                    const { value } = target;
                                    handleChange({
                                      target: {
                                        name: "quora_pixel_id",
                                        value,
                                      },
                                    });
                                  }}
                                />
                              </div>
                            </div>

                            <div className="rd_flexpoarttpixelreta">
                              <div className="rd_flexpoarttpixelretaitem">
                                <p>
                                  <span className="rd_retagpixico rd_retagpixico-Bing"></span>
                                  Bing <Tooltip code="P22F10" />
                                </p>
                              </div>
                              <div className="rd_flexpoarttpixelretaitem">
                                <FormField
                                  type="text"
                                  name="bing_pixel_id"
                                  placeholder="Pixel Id"
                                  fieldClasses="rd_adddayofinput"
                                  value={values.bing_pixel_id}
                                  errors={errors}
                                  touched={touched}
                                  onChange={({ target }) => {
                                    const { value } = target;
                                    handleChange({
                                      target: {
                                        name: "bing_pixel_id",
                                        value,
                                      },
                                    });
                                  }}
                                />
                              </div>
                            </div>

                            <div className="rd_flexpoarttpixelreta">
                              <div className="rd_flexpoarttpixelretaitem">
                                <p>
                                  <span className="rd_retagpixico rd_retagpixico-googleadwords"></span>
                                  Google Adwords <Tooltip code="P22F11" />
                                </p>
                              </div>
                              <div className="rd_flexpoarttpixelretaitem">
                                <FormField
                                  type="text"
                                  name="google_adwords_pixel_id"
                                  placeholder="Pixel Id"
                                  fieldClasses="rd_adddayofinput"
                                  value={values.google_adwords_pixel_id}
                                  errors={errors}
                                  touched={touched}
                                  onChange={({ target }) => {
                                    const { value } = target;
                                    handleChange({
                                      target: {
                                        name: "google_adwords_pixel_id",
                                        value,
                                      },
                                    });
                                  }}
                                />
                              </div>
                            </div>

                            <div className="rd_flexpoarttpixelreta">
                              <div className="rd_flexpoarttpixelretaitem">
                                <p>
                                  <span className="rd_retagpixico rd_retagpixico-googletagmanager"></span>
                                  Google Tag Manager <Tooltip code="P22F12" />
                                </p>
                              </div>
                              <div className="rd_flexpoarttpixelretaitem">
                                <FormField
                                  type="text"
                                  name="google_tm_pixel_id"
                                  placeholder="Pixel Id"
                                  fieldClasses="rd_adddayofinput"
                                  value={values.google_tm_pixel_id}
                                  errors={errors}
                                  touched={touched}
                                  onChange={({ target }) => {
                                    const { value } = target;
                                    handleChange({
                                      target: {
                                        name: "google_tm_pixel_id",
                                        value,
                                      },
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="rd_vacationfilterpart">
                        <div className="rd_flexpoarttpixelreta rd_flexpoarttpixelretafull">
                          <div className="rd_flexpoarttpixelretaitem">
                            <label className="form-label">
                              Google Analytics <Tooltip code="P22F13" />
                            </label>
                            <FormField
                              type="text"
                              name="google_analytics_id"
                              placeholder="Google analytics ID"
                              fieldClasses="rd_adddayofinput"
                              value={values.google_analytics_id}
                              errors={errors}
                              touched={touched}
                              onChange={({ target }) => {
                                const { value } = target;
                                handleChange({
                                  target: {
                                    name: "google_analytics_id",
                                    value,
                                  },
                                });
                              }}
                            />
                          </div>
                        </div>

                        <Col lg="12" md="12" sm="12">
                          <Button
                            style={{ marginBottom: "10px" }}
                            type="submit"
                            className="btn btn-app col-md-2 col-lg-2 col-sm-12 float-right"
                          >
                            Save
                          </Button>
                        </Col>
                      </div>
                    </Form>
                  </>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </section>
  );
};

const ConnectedSEO = connect(null, {
  getSEO,
  saveSEO,
})(SEO);

const SEOGuarded = (props) => {
  useFeatureRedirect(
    FEATURE_IDS.MARKETING_TOOLS,
    "/admin/manage-subscription"
  );
  return <ConnectedSEO {...props} />;
};

export default SEOGuarded;
